import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useStatus } from './StatusContext';
import { DashboardApi, DashboardConfig } from '../services/api';

const RealtimeContext = createContext(null);

const shortChatId = (chatId) => (chatId ? String(chatId).slice(0, 8) : '');
const AGENT_AVAILABILITY_STORAGE_PREFIX = 'dashboard:agent-desired-online';
const AGENT_AVAILABILITY_TTL_MS = 60 * 60 * 1000;
const LIVE_CONNECTION_GRACE_MS = 5_000;

const buildAgentAvailabilityStorageKey = (userId, companyId) => {
  if (!userId || !companyId) return null;
  return `${AGENT_AVAILABILITY_STORAGE_PREFIX}:${userId}:${companyId}`;
};

const readStoredAgentAvailability = (userId, companyId) => {
  if (typeof window === 'undefined') return false;
  const key = buildAgentAvailabilityStorageKey(userId, companyId);
  if (!key) return false;
  const raw = window.localStorage.getItem(key);
  if (!raw) return false;

  if (raw === 'true') {
    return true;
  }

  try {
    const parsed = JSON.parse(raw);
    const enabled = Boolean(parsed?.enabled);
    const expiresAt = Number(parsed?.expiresAt || 0);
    if (!enabled) return false;
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      window.localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch (_error) {
    window.localStorage.removeItem(key);
    return false;
  }
};

const writeStoredAgentAvailability = (userId, companyId, value) => {
  if (typeof window === 'undefined') return;
  const key = buildAgentAvailabilityStorageKey(userId, companyId);
  if (!key) return;
  window.localStorage.setItem(key, JSON.stringify({
    enabled: Boolean(value),
    expiresAt: value ? Date.now() + AGENT_AVAILABILITY_TTL_MS : 0,
  }));
};

const requestBrowserNotificationPermission = async () => {
  if (typeof window === 'undefined' || !window.Notification) return null;
  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }
  try {
    return await window.Notification.requestPermission();
  } catch (_error) {
    return window.Notification.permission;
  }
};

const showBrowserNotification = ({ title, message, href }) => {
  if (typeof window === 'undefined' || !window.Notification) return;
  if (window.Notification.permission !== 'granted') return;

  const notification = new window.Notification(title, {
    body: message,
    tag: href || title,
  });
  notification.onclick = () => {
    window.focus?.();
    if (href) {
      window.location.href = href;
    }
    notification.close();
  };
};

export function RealtimeProvider({ children }) {
  const { selectedCompanyId, permissions, user } = useAuth();
  const { addStatus } = useStatus();
  const socketRef = useRef(null);
  const connectionGraceTimerRef = useRef(null);
  const connectionNoticeShownRef = useRef(false);
  const lastConnectionIssueRef = useRef(null);
  const myChatsRef = useRef([]);
  const pendingQueueRef = useRef([]);
  const activeAgentChatIdRef = useRef(null);
  const desiredAgentAvailableRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [socketReady, setSocketReady] = useState(false);
  const [agentDesiredAvailable, setAgentDesiredAvailable] = useState(false);
  const [agentIsAvailable, setAgentIsAvailable] = useState(false);
  const [agentIsOnline, setAgentIsOnline] = useState(false);
  const [agentAvailabilityLoading, setAgentAvailabilityLoading] = useState(false);
  const [myChats, setMyChats] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [incomingAgentOffer, setIncomingAgentOffer] = useState(null);
  const [agentQueuesLoading, setAgentQueuesLoading] = useState(false);
  const [activeAgentChatId, setActiveAgentChatIdState] = useState(null);

  const shouldConnect = Boolean(
    selectedCompanyId
    && (permissions?.canViewAgent || permissions?.canViewInnsikt || permissions?.canViewBots),
  );
  const canUseAgent = Boolean(selectedCompanyId && permissions?.canViewAgent);

  const addLiveStatus = useCallback((payload) => {
    addStatus({
      source: 'Live',
      durationMs: 5_000,
      ...payload,
    });
  }, [addStatus]);

  const clearConnectionGraceTimer = useCallback(() => {
    if (connectionGraceTimerRef.current !== null) {
      window.clearTimeout(connectionGraceTimerRef.current);
      connectionGraceTimerRef.current = null;
    }
  }, []);

  const scheduleConnectionNotice = useCallback((payload) => {
    lastConnectionIssueRef.current = payload;
    if (connectionGraceTimerRef.current !== null) return;
    connectionGraceTimerRef.current = window.setTimeout(() => {
      connectionGraceTimerRef.current = null;
      if (connectionNoticeShownRef.current) return;
      if (socketRef.current?.connected) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      connectionNoticeShownRef.current = true;
      addLiveStatus(payload);
    }, LIVE_CONNECTION_GRACE_MS);
  }, [addLiveStatus]);

  const resolveConnectionIssue = useCallback(() => {
    clearConnectionGraceTimer();
    lastConnectionIssueRef.current = null;
    const shouldAnnounceRecovery = connectionNoticeShownRef.current;
    connectionNoticeShownRef.current = false;
    return shouldAnnounceRecovery;
  }, [clearConnectionGraceTimer]);

  const refreshAgentQueues = useCallback(async () => {
    if (!canUseAgent) {
      setPendingQueue([]);
      setMyChats([]);
      pendingQueueRef.current = [];
      myChatsRef.current = [];
      return { pendingQueue: [], myChats: [] };
    }

    setAgentQueuesLoading(true);
    try {
      const [queue, chats] = await Promise.all([
        DashboardApi.getAgentQueue(selectedCompanyId),
        DashboardApi.getAgentMyChats(selectedCompanyId),
      ]);
      const nextPendingQueue = Array.isArray(queue) ? queue : [];
      const nextMyChats = Array.isArray(chats) ? chats : [];
      setPendingQueue(nextPendingQueue);
      setMyChats(nextMyChats);
      pendingQueueRef.current = nextPendingQueue;
      myChatsRef.current = nextMyChats;
      return { pendingQueue: nextPendingQueue, myChats: nextMyChats };
    } catch (error) {
      console.warn('[Realtime] Failed to refresh agent queues:', error);
      return { pendingQueue: pendingQueueRef.current, myChats: myChatsRef.current };
    } finally {
      setAgentQueuesLoading(false);
    }
  }, [canUseAgent, selectedCompanyId]);

  useEffect(() => {
    myChatsRef.current = myChats;
  }, [myChats]);

  useEffect(() => {
    pendingQueueRef.current = pendingQueue;
  }, [pendingQueue]);

  useEffect(() => {
    activeAgentChatIdRef.current = activeAgentChatId;
  }, [activeAgentChatId]);

  useEffect(() => {
    desiredAgentAvailableRef.current = agentDesiredAvailable;
  }, [agentDesiredAvailable]);

  const emit = useCallback((event, data) => {
    return new Promise((resolve, reject) => {
      const socket = socketRef.current;

      if (!socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      let settled = false;
      const timeout = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error('Socket emit timeout'));
        }
      }, 30000);

      socket.emit(event, data, (response) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        if (response?.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      });
    });
  }, []);

  const setActiveAgentChatId = useCallback((chatId) => {
    const nextChatId = chatId ? String(chatId) : null;
    activeAgentChatIdRef.current = nextChatId;
    setActiveAgentChatIdState(nextChatId);
  }, []);

  useEffect(() => {
    if (!canUseAgent) {
      setAgentDesiredAvailable(false);
      setAgentIsAvailable(false);
      setAgentIsOnline(false);
      setPendingQueue([]);
      setMyChats([]);
      pendingQueueRef.current = [];
      myChatsRef.current = [];
      return undefined;
    }

    let mounted = true;
    const storedDesired = readStoredAgentAvailability(user?.userId, selectedCompanyId);
    setAgentDesiredAvailable(storedDesired);
    setAgentIsAvailable(storedDesired);
    desiredAgentAvailableRef.current = storedDesired;
    setAgentAvailabilityLoading(true);

    DashboardApi.getAgentAvailability(selectedCompanyId)
      .then((data) => {
        if (!mounted) return;
        setAgentIsOnline(Boolean(data?.isOnline));
        setAgentIsAvailable(Boolean(data?.isAvailable || storedDesired));
      })
      .catch((error) => {
        console.warn('[Realtime] Failed to load agent availability:', error);
        if (mounted) {
          setAgentIsOnline(false);
          setAgentIsAvailable(storedDesired);
        }
      })
      .finally(() => {
        if (mounted) setAgentAvailabilityLoading(false);
      });

    refreshAgentQueues();

    return () => {
      mounted = false;
    };
  }, [canUseAgent, refreshAgentQueues, selectedCompanyId, user?.userId]);

  const applyAgentAvailability = useCallback(async (isAvailable) => {
    if (!canUseAgent) {
      throw new Error('Agenttilgang mangler');
    }

    const nextAvailable = Boolean(isAvailable);
    setAgentDesiredAvailable(nextAvailable);
    setAgentIsAvailable(nextAvailable);
    writeStoredAgentAvailability(user?.userId, selectedCompanyId, nextAvailable);

    if (nextAvailable) {
      requestBrowserNotificationPermission();
    }

    try {
      const response = await emit('set_availability', { isAvailable: nextAvailable });
      setAgentIsAvailable(Boolean(response?.isAvailable ?? nextAvailable));
      setAgentIsOnline(true);
      if (nextAvailable) {
        writeStoredAgentAvailability(user?.userId, selectedCompanyId, true);
        refreshAgentQueues();
      }
      return response;
    } catch (error) {
      setAgentIsOnline(false);
      if (!nextAvailable) {
        setAgentIsAvailable(false);
      }
      if (nextAvailable) {
        setAgentIsAvailable(true);
        addLiveStatus({
          type: 'warning',
          title: 'Agentstatus lagret',
          message: 'Dashboardet prøver å koble agenten til igjen automatisk.',
          dedupeKey: 'live:agent-availability-retry',
        });
        return { success: false, retrying: true };
      }
      throw error;
    }
  }, [addLiveStatus, canUseAgent, emit, refreshAgentQueues, selectedCompanyId, user?.userId]);

  const shouldNotifyForCustomerMessage = useCallback((data = {}) => {
    if (!data.chatId) return false;
    const chatId = String(data.chatId);
    if (activeAgentChatIdRef.current && String(activeAgentChatIdRef.current) === chatId) {
      return false;
    }
    return myChatsRef.current.some((chat) => String(chat.chatId) === chatId);
  }, []);

  const notifyCustomerMessage = useCallback((data = {}) => {
    if (!shouldNotifyForCustomerMessage(data)) return;

    const href = data.chatId ? `/agent?chat=${encodeURIComponent(data.chatId)}` : '/agent';
    const title = 'Ny kundemelding';
    const message = data.message || `Ny melding i samtale ${shortChatId(data.chatId)}.`;
    addLiveStatus({
      type: 'info',
      title,
      message,
      actionLabel: 'Åpne samtalen',
      actionHref: href,
      dedupeKey: data.chatId ? `live:customer-message:${data.chatId}` : 'live:customer-message',
    });

    if (typeof document !== 'undefined' && (document.hidden || !document.hasFocus?.())) {
      showBrowserNotification({ title, message, href });
    }
  }, [addLiveStatus, shouldNotifyForCustomerMessage]);

  useEffect(() => {
    if (!shouldConnect) {
      setIsConnected(false);
      setConnectionState('disconnected');
      setSocketReady(false);
      return undefined;
    }

    const token = DashboardConfig.getAuthToken?.();
    if (!token) {
      setConnectionState('disconnected');
      return undefined;
    }

    setConnectionState('connecting');
    const baseUrl = (DashboardConfig.apiBaseUrl || '')
      .replace(/\/api\/?$/, '')
      .replace(/\/+$/u, '');

    const socket = io(`${baseUrl}/agent`, {
      auth: { token, companyId: selectedCompanyId },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    socketRef.current = socket;
    setSocketReady(true);

    socket.on('connect', () => {
      setIsConnected(true);
      setConnectionState('connected');
      setAgentIsOnline(true);
      if (resolveConnectionIssue()) {
        addLiveStatus({
          type: 'success',
          title: 'Live-tilkobling gjenopprettet',
          message: 'Dashboardet mottar oppdateringer igjen.',
          dedupeKey: 'live:reconnected',
        });
      }
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      setConnectionState('disconnected');
      setAgentIsOnline(false);
      scheduleConnectionNotice({
        type: 'warning',
        title: 'Live-tilkobling brutt',
        message: reason === 'io client disconnect'
          ? 'Live-tilkoblingen ble lukket.'
          : 'Dashboardet prøver å koble til igjen.',
        dedupeKey: 'live:disconnected',
      });
    });

    socket.on('connect_error', (error) => {
      setConnectionState('error');
      scheduleConnectionNotice({
        type: 'warning',
        title: 'Live-tilkobling feilet',
        message: error?.message || 'Kunne ikke koble til sanntidsoppdateringer.',
        dedupeKey: 'live:connect-error',
      });
    });

    socket.io.on('reconnect_attempt', () => {
      setConnectionState('connecting');
    });

    socket.io.on('reconnect', () => {
      setIsConnected(true);
      setConnectionState('connected');
      setAgentIsOnline(true);
    });

    socket.on('chat_offer', (data = {}) => {
      setIncomingAgentOffer(data);
      refreshAgentQueues();
      addLiveStatus({
        type: 'warning',
        title: 'Ny samtale venter',
        message: data?.preview?.lastMessage || `Samtale ${shortChatId(data.chatId)} trenger en agent.`,
        actionLabel: 'Åpne agentpanelet',
        actionHref: data.chatId ? `/agent?offer=${encodeURIComponent(data.chatId)}` : '/agent',
        dedupeKey: data.chatId ? `live:chat-offer:${data.chatId}` : 'live:chat-offer',
      });
    });

    socket.on('chat_offer_expired', (data = {}) => {
      setIncomingAgentOffer((current) => (
        current?.chatId === data.chatId ? null : current
      ));
      refreshAgentQueues();
    });

    socket.on('chat_assigned', (data = {}) => {
      setIncomingAgentOffer((current) => (
        current?.chatId === data.chatId ? null : current
      ));
      if (data.chatId) {
        setMyChats((current) => {
          if (current.some((chat) => String(chat.chatId) === String(data.chatId))) {
            return current;
          }
          const next = [{
            chatId: data.chatId,
            companyId: data.companyId || selectedCompanyId,
            originChannel: data.originChannel || 'Web',
            lastMessage: data.message || null,
            lastMessageAt: data.timestamp || new Date().toISOString(),
            messageCount: data.messageCount || 0,
          }, ...current];
          myChatsRef.current = next;
          return next;
        });
      }
      refreshAgentQueues();
      addLiveStatus({
        type: 'success',
        title: 'Samtale tildelt deg',
        message: `Samtale ${shortChatId(data.chatId)} er klar i agentpanelet.`,
        actionLabel: 'Åpne samtalen',
        actionHref: data.chatId ? `/agent?chat=${encodeURIComponent(data.chatId)}` : '/agent',
        dedupeKey: data.chatId ? `live:chat-assigned:${data.chatId}` : 'live:chat-assigned',
      });
    });

    socket.on('new_customer_message', (data = {}) => {
      refreshAgentQueues();
      notifyCustomerMessage(data);
    });

    socket.on('pending_chat_added', (data = {}) => {
      refreshAgentQueues();
      addLiveStatus({
        type: 'info',
        title: 'Ny samtale i kø',
        message: `En samtale ${data.chatId ? `(${shortChatId(data.chatId)}) ` : ''}kan tas av en ledig agent.`,
        actionLabel: 'Åpne agentpanelet',
        actionHref: '/agent?tab=queue',
        dedupeKey: data.chatId ? `live:pending-chat:${data.chatId}` : 'live:pending-chat',
      });
    });

    socket.on('chat_claimed', (data = {}) => {
      setIncomingAgentOffer((current) => (
        current?.chatId === data.chatId ? null : current
      ));
      refreshAgentQueues();
      const claimedBySelf = data.agentId && user?.userId && String(data.agentId) === String(user.userId);
      addLiveStatus({
        type: claimedBySelf ? 'success' : 'info',
        title: claimedBySelf ? 'Du tok samtalen' : 'Samtale tatt',
        message: claimedBySelf
          ? `Samtale ${shortChatId(data.chatId)} er tildelt deg.`
          : `${data.agentName || 'En agent'} tok samtale ${shortChatId(data.chatId)}.`,
        actionLabel: claimedBySelf ? 'Åpne samtalen' : null,
        actionHref: claimedBySelf && data.chatId ? `/agent?chat=${encodeURIComponent(data.chatId)}` : null,
        dedupeKey: data.chatId ? `live:chat-claimed:${data.chatId}` : 'live:chat-claimed',
      });
    });

    socket.on('chat_handed_back', (data = {}) => {
      refreshAgentQueues();
      if (activeAgentChatIdRef.current && String(activeAgentChatIdRef.current) === String(data.chatId)) {
        setActiveAgentChatId(null);
      }
    });

    socket.on('chat_closed', (data = {}) => {
      refreshAgentQueues();
      if (activeAgentChatIdRef.current && String(activeAgentChatIdRef.current) === String(data.chatId)) {
        setActiveAgentChatId(null);
      }
      addLiveStatus({
        type: 'success',
        title: 'Samtale lukket',
        message: `Samtale ${shortChatId(data.chatId)} er avsluttet.`,
        dedupeKey: data.chatId ? `live:chat-closed:${data.chatId}` : 'live:chat-closed',
      });
    });

    socket.on('inquiry_created', (data = {}) => {
      const inquiryId = data.inquiryId || data.id;
      addLiveStatus({
        type: data.status === 'delivery_failed' ? 'warning' : 'info',
        title: data.status === 'delivery_failed' ? 'Henvendelse trenger oppfølging' : 'Ny henvendelse',
        message: data.subject || data.summary || 'En ny henvendelse er registrert.',
        actionLabel: 'Åpne henvendelsen',
        actionHref: inquiryId ? `/henvendelser/${inquiryId}` : '/henvendelser',
        dedupeKey: inquiryId ? `live:inquiry:${inquiryId}` : 'live:inquiry',
      });
    });

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      if (connectionNoticeShownRef.current) return;
      const socket = socketRef.current;
      if (!socket || socket.connected) return;
      scheduleConnectionNotice(lastConnectionIssueRef.current || {
        type: 'warning',
        title: 'Live-tilkobling brutt',
        message: 'Dashboardet prøver å koble til igjen.',
        dedupeKey: 'live:disconnected',
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearConnectionGraceTimer();
      connectionNoticeShownRef.current = false;
      lastConnectionIssueRef.current = null;
      socket.removeAllListeners();
      socket.io.off('reconnect_attempt');
      socket.io.off('reconnect');
      socket.disconnect();
      socketRef.current = null;
      setSocketReady(false);
      setIsConnected(false);
      setConnectionState('disconnected');
      setAgentIsOnline(false);
    };
  }, [
    addLiveStatus,
    clearConnectionGraceTimer,
    notifyCustomerMessage,
    refreshAgentQueues,
    resolveConnectionIssue,
    scheduleConnectionNotice,
    selectedCompanyId,
    setActiveAgentChatId,
    shouldConnect,
    user?.userId,
  ]);

  useEffect(() => {
    if (!canUseAgent || !socketReady || !isConnected) return;

    refreshAgentQueues();
    if (desiredAgentAvailableRef.current) {
      emit('set_availability', { isAvailable: true })
        .then((response) => {
          setAgentIsAvailable(Boolean(response?.isAvailable ?? true));
          setAgentIsOnline(true);
          writeStoredAgentAvailability(user?.userId, selectedCompanyId, true);
        })
        .catch((error) => {
          console.warn('[Realtime] Failed to restore agent availability:', error);
          setAgentIsOnline(false);
          setAgentIsAvailable(true);
          addLiveStatus({
            type: 'warning',
            title: 'Kunne ikke koble agenten til',
            message: 'Agentstatus er fortsatt aktivert. Dashboardet prøver igjen automatisk.',
            actionLabel: 'Åpne agentpanelet',
            actionHref: '/agent',
            dedupeKey: 'live:agent-availability-restore',
          });
        });
    }
  }, [addLiveStatus, canUseAgent, emit, isConnected, refreshAgentQueues, selectedCompanyId, socketReady, user?.userId]);

  useEffect(() => {
    if (!canUseAgent || !isConnected || !agentDesiredAvailable) return undefined;
    const timer = window.setInterval(() => {
      emit('set_availability', { isAvailable: true })
        .then(() => {
          setAgentIsAvailable(true);
          setAgentIsOnline(true);
          writeStoredAgentAvailability(user?.userId, selectedCompanyId, true);
        })
        .catch((error) => {
          console.warn('[Realtime] Availability ensure failed:', error);
          setAgentIsAvailable(true);
          setAgentIsOnline(false);
        });
    }, 20_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [agentDesiredAvailable, canUseAgent, emit, isConnected, selectedCompanyId, user?.userId]);

  useEffect(() => {
    if (!canUseAgent || !isConnected) return undefined;
    const timer = window.setInterval(() => {
      refreshAgentQueues();
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [canUseAgent, isConnected, refreshAgentQueues]);

  useEffect(() => {
    if (!canUseAgent) return undefined;
    const handleFocus = () => {
      refreshAgentQueues();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [canUseAgent, refreshAgentQueues]);

  const on = useCallback((event, handler) => {
    const socket = socketRef.current;
    if (!socket) return () => {};
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, []);

  const off = useCallback((event, handler) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (handler) {
      socket.off(event, handler);
    } else {
      socket.off(event);
    }
  }, []);

  const value = useMemo(() => ({
    socketReady,
    isConnected,
    connectionState,
    on,
    off,
    emit,
    agentDesiredAvailable,
    agentIsAvailable,
    agentIsOnline,
    agentAvailabilityLoading,
    myChats,
    pendingQueue,
    incomingAgentOffer,
    agentQueuesLoading,
    activeAgentChatId,
    setActiveAgentChatId,
    refreshAgentQueues,
    setAvailability: applyAgentAvailability,
    acceptChat: (chatId) => emit('accept_chat', { chatId }),
    declineChat: (chatId) => emit('decline_chat', { chatId }),
    sendMessage: (chatId, message) => emit('send_message', { chatId, message }),
    sendTyping: (chatId, isTyping) => {
      const socket = socketRef.current;
      if (!socket?.connected) return;
      socket.emit('typing', { chatId, isTyping });
    },
    handBackToAI: (chatId) => emit('hand_back_to_ai', { chatId }),
    resolveChat: (chatId) => emit('resolve_chat', { chatId }),
  }), [
    activeAgentChatId,
    agentAvailabilityLoading,
    agentDesiredAvailable,
    agentIsAvailable,
    agentIsOnline,
    agentQueuesLoading,
    applyAgentAvailability,
    connectionState,
    emit,
    isConnected,
    myChats,
    off,
    on,
    pendingQueue,
    incomingAgentOffer,
    refreshAgentQueues,
    setActiveAgentChatId,
    socketReady,
  ]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useDashboardRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useDashboardRealtime must be used within a RealtimeProvider');
  }
  return context;
}
